/** Presentation helpers for the notification-subscriptions grid + drawer. */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

import type { SubscriptionMode, SubscriptionStatus } from '@/api/notificationSubscriptions';

/** The canonical notification types, for local suggestion lists + the create
 *  dialog fallback. The authoritative per-restech set comes from the
 *  integration catalog's flat `notification_types` field. */
export const SUBSCRIPTION_TYPES = [
  'BOOKING_UPDATE',
  'ORDER_UPDATE',
  'PRODUCT_UPDATE',
  'AVAILABILITY_UPDATE',
];

export const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  'pending',
  'active',
  'create_failed',
  'disabled',
  'deleting',
  'delete_failed',
];

export const SUBSCRIPTION_MODES: SubscriptionMode[] = ['log_only', 'process'];

const STATUS_COLORS: Record<SubscriptionStatus, ChipProps['color']> = {
  pending: 'default',
  active: 'success',
  create_failed: 'error',
  disabled: 'warning',
  deleting: 'default',
  delete_failed: 'error',
};

/** Subscription status as a colored chip. */
export function StatusChip({ status }: { status: SubscriptionStatus }) {
  const color = STATUS_COLORS[status] ?? 'default';
  return (
    <Chip
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      label={status.replace(/_/g, ' ')}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** Delivery mode chip: `process` filled (live), `log_only` outlined (ramp). */
export function ModeChip({ mode }: { mode: SubscriptionMode }) {
  return mode === 'process' ? (
    <Chip size="small" color="primary" label="process" sx={{ height: 22, fontSize: 12 }} />
  ) : (
    <Chip size="small" variant="outlined" label="log only" sx={{ height: 22, fontSize: 12 }} />
  );
}

/** Account slug — the empty string is the default account. */
export function AccountChip({ account }: { account: string }) {
  return account ? (
    <Chip
      size="small"
      variant="outlined"
      label={account}
      sx={{ height: 22, fontSize: 12, fontFamily: 'monospace' }}
    />
  ) : (
    <Chip size="small" variant="outlined" color="default" label="default" sx={{ height: 22, fontSize: 12, fontStyle: 'italic' }} />
  );
}
